import { Button, List, message, Select, Space, Typography } from 'antd';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import Form from '../../components/Form';
import GeneralRequestJobPostingForm, {
  GeneralRequestJobPostingFormValues,
} from '../../components/GeneralRequestJobPostingForm';
import Page from '../../components/Page';
import PageContent from '../../components/PageContent';
import PageTitle from '../../components/PageTitle';
import { fixUnorderedListInQuill } from '../../components/RichEditor/RichEditor';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import {
  PaginatedResponse,
  VixtingRequestJobPostingCollection,
  RequestJobPostingId,
} from '../../types';

const STALE_TIME = 1000 * 60 * 5;

const EditRequestJobPosting = () => {
  const { t } = useTranslation();

  const { tenant, id } = useTypedParams({ tenant: String, id: RequestJobPostingId });
  const navigate = useTypedNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [setIsDirty] = useDiscardChangesModal(form);
  const selectedCompanyValue = Form.useWatch<string>('vixtingCompany', form);
  const selectedJobRoleValue = Form.useWatch<string>('vixtingJobRole', form);

  const requestJobPosting = useAPIQuery(`/request-job-postings/${id}/`);

  const requestJobPostingUsersPages = useInfiniteAPIQuery([
    '/request-job-posting-users/',
    {
      params: {
        request_job_posting_id: id,
      },
    },
  ]);

  const requestJobPostingUsers = usePrefetchPages(
    requestJobPostingUsersPages,
    (data) => data.results
  );

  const usersPages = useInfiniteAPIQuery([
    '/users/',
    {
      params: {
        is_active: true,
      },
    },
  ]);
  const users = usePrefetchPages(usersPages, (data) => data.results);

  const config = useAPIQuery('/configs/tenant-configurations/');

  const vixtingRequestJobPosting = useAPIQuery(
    [
      '/vixting/vixting-request-job-postings/',
      {
        params: {
          request_job_posting_id: id,
        },
      },
    ],
    {
      enabled: config.data?.enableVixtingAdmission,
      select(data: PaginatedResponse<VixtingRequestJobPostingCollection>) {
        return data.count > 0 ? data.results[0] : null;
      },
    }
  );

  const vixtingCompaniesPages = useInfiniteAPIQuery(
    [
      '/vixting/vixting-companies/',
      {
        params: {
          is_active: true,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar empresas!'));
      },
    }
  );
  const vixtingCompanies = usePrefetchPages(vixtingCompaniesPages, (data) => data.results);

  const vixtingAffiliatesPages = useInfiniteAPIQuery(
    [
      '/vixting/affiliates/',
      {
        params: {
          company_id: selectedCompanyValue,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!selectedCompanyValue,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar filiais da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );

  const vixtingAffiliates = usePrefetchPages(vixtingAffiliatesPages, (data) => data.results);

  const vixtingUnitBusinessesPages = useInfiniteAPIQuery(
    [
      '/vixting/unit-businesses/',
      {
        params: {
          company_id: selectedCompanyValue,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!config.data?.enableVixtingUnitBusiness &&
        !!selectedCompanyValue,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar unidades de negócio da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingUnitBusinesses = usePrefetchPages(
    vixtingUnitBusinessesPages,
    (data) => data.results
  );

  const vixtingJobRolesPages = useInfiniteAPIQuery(
    [
      '/vixting/job-roles/',
      {
        params: {
          company_id: selectedCompanyValue,
        },
      },
    ],
    {
      enabled: !!config.data?.enableVixtingAdmission && !!selectedCompanyValue,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar cargos da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingJobRoles = usePrefetchPages(vixtingJobRolesPages, (data) => data.results);

  const selectedJob = vixtingJobRoles.find(
    (jobRole) => jobRole.id?.toString() === selectedJobRoleValue?.toString()
  );

  const vixtingDepartmentsPages = useInfiniteAPIQuery(
    [
      '/vixting/departments/',
      {
        params: {
          company_id: selectedCompanyValue,
        },
      },
    ],
    {
      enabled:
        !!config.data?.enableVixtingAdmission &&
        !!selectedCompanyValue &&
        !!selectedJob?.linkToAllDepartment,
      staleTime: STALE_TIME,
      onError: () => {
        message.error(t('Falha ao carregar departamentos da vixting!'));
      },
      pageKey: 'offsetPage',
    }
  );
  const vixtingAllDepartments = usePrefetchPages(vixtingDepartmentsPages, (data) => data.results);

  const mutation = useAPIMutation(
    async (variables: GeneralRequestJobPostingFormValues, api) => {
      const updatedRequestJobPosting = await api.patch(`/request-job-postings/${id}/`, {
        name: variables.name,
        type: variables.requestType,
        employee_name_being_replaced:
          variables.requestType === 'SUBSTITUTION' ? variables.employeeNameBeingReplaced : '',
        positions: variables.positions,
        start_salary: variables.startSalaryRange,
        end_salary: variables.endSalaryRange,
        branch: variables.branch,
        description: variables.description,
      });

      const existingRequestJobPostingUsers = requestJobPostingUsers.map((obj) => obj.user);
      const newRequestJobPostingUsers = variables.relatedUsers || [];

      const requestJobPostingUsersToCreate = newRequestJobPostingUsers.filter(
        (id) => !existingRequestJobPostingUsers.includes(id)
      );
      const requestJobPostingUsersToDelete = requestJobPostingUsers.filter(
        (obj) => !newRequestJobPostingUsers.includes(obj.user)
      );

      for (const user_id of requestJobPostingUsersToCreate) {
        await api.post('/request-job-posting-users/', {
          request_job_posting: updatedRequestJobPosting.data.id,
          user: user_id,
        });
      }

      for (const obj of requestJobPostingUsersToDelete) {
        await api.delete(`/request-job-posting-users/${obj.id}/`);
      }

      if (config.data?.enableVixtingAdmission) {
        if (vixtingRequestJobPosting.data) {
          await api.patch(
            `/vixting/vixting-request-job-postings/${vixtingRequestJobPosting.data?.id}/`,
            {
              company: variables.vixtingCompany || '',
              job_role: variables.vixtingJobRole || '',
              department: variables.vixtingDepartment || '',
              affiliate: variables.vixtingAffiliate || '',
              unit_business: variables.vixtingUnitBusiness || '',
              last_created_history_id:
                updatedRequestJobPosting.data?.last_created_history_id || null,
            }
          );
        } else {
          await api.post(`/vixting/vixting-request-job-postings/`, {
            request_job_posting: id,
            company: variables.vixtingCompany || '',
            job_role: variables.vixtingJobRole || '',
            department: variables.vixtingDepartment || '',
            affiliate: variables.vixtingAffiliate || '',
            unit_business: variables.vixtingUnitBusiness || '',
          });
        }
      }

      return updatedRequestJobPosting;
    },
    {
      onSettled: () => queryClient.invalidateQueries('/request-job-postings/'),
      onSuccess: (response) => {
        setIsDirty(false);
        // dispatch setIsDirty possui um delay para atualizar o estado do Modal de descarte, resolvido com uma mensagem antes do navigate
        message.success(t('Requisição de vaga editada'));
        navigate({
          pathname: '/:tenant/request-job-postings/:id/analysis/',
          params: { tenant, id: response.data.id },
        });
      },
    }
  );

  const formArgs = {
    enableVixtingAdmission: config.data?.enableVixtingAdmission,
    enableVixtingUnitBusiness: config.data?.enableVixtingUnitBusiness,
    vixtingCompanies,
    vixtingJobRoles,
    vixtingAffiliates,
    vixtingUnitBusinesses,
    vixtingDepartments: selectedJob?.linkToAllDepartment
      ? vixtingAllDepartments
      : selectedJob?.departments || [],
  };

  return (
    <Page>
      <PageTitle title={t('Editar Requisição')} />
      <PageContent>
        <Form
          layout="vertical"
          initialValues={{ templateName: requestJobPosting.data?.template_name }}
        >
          <Form.Col span={12}>
            <Form.Item
              name="templateName"
              label={t('Modelo de requisição')}
              rules={[{ required: true }]}
            >
              <Select disabled>
                <Select.Option value={requestJobPosting.data?.template_name}>
                  {requestJobPosting.data?.template_name}
                </Select.Option>
              </Select>
            </Form.Item>
            <List
              size="small"
              dataSource={requestJobPosting.data?.stages}
              renderItem={(item) => (
                <List.Item>
                  <Space direction="vertical">
                    <Typography.Text>
                      {(item.sequence || 0) + 1}. {item.name}
                    </Typography.Text>
                    <Typography.Text>
                      {users.find((user) => user.id === item.approver.id)?.full_name || '-'}
                    </Typography.Text>
                  </Space>
                </List.Item>
              )}
              style={{ marginBottom: 24 }}
            />
          </Form.Col>
        </Form>
        <Form<GeneralRequestJobPostingFormValues>
          form={form}
          name="basic"
          layout="vertical"
          validateMessages={{
            required: 'Campo obrigatório.',
          }}
          initialValues={{
            requestType: requestJobPosting.data?.type,
            positions: requestJobPosting.data?.positions,
            employeeNameBeingReplaced: requestJobPosting.data?.employee_name_being_replaced,
            name: requestJobPosting.data?.name,
            startSalaryRange: requestJobPosting.data?.start_salary,
            endSalaryRange: requestJobPosting.data?.end_salary,
            branch: requestJobPosting.data?.branch,
            description: requestJobPosting.data?.description,
            relatedUsers: requestJobPostingUsers.map((obj) => obj.user),
            annotation: requestJobPosting.data?.claimant_commentary,
            vixtingCompany: vixtingRequestJobPosting.data?.company,
            vixtingAffiliate: vixtingRequestJobPosting.data?.affiliate,
            vixtingUnitBusiness: vixtingRequestJobPosting.data?.unit_business,
            vixtingJobRole: vixtingRequestJobPosting.data?.job_role,
            vixtingDepartment: vixtingRequestJobPosting.data?.department,
          }}
          onFinish={(variables) => {
            mutation.mutate({
              ...variables,
              description: fixUnorderedListInQuill(variables.description ?? ''),
            });
          }}
          onValuesChange={() => setIsDirty(form.isFieldsTouched())}
        >
          <GeneralRequestJobPostingForm
            form={form}
            users={users}
            disableCommentaryField
            disableReplacedEmployeeNameField={
              !!requestJobPosting.data?.employee_name_being_replaced
            }
            {...formArgs}
          />
          <Form.Footer>
            <Form.FooterEnd>
              <Button
                onClick={() => {
                  navigate({
                    pathname: '/:tenant/request-job-postings/:id/analysis/',
                    params: { tenant, id },
                  });
                }}
              >
                <Trans>Cancelar</Trans>
              </Button>
              <Button loading={mutation.isLoading} type="primary" htmlType="submit">
                <Trans>Salvar</Trans>
              </Button>
            </Form.FooterEnd>
          </Form.Footer>
        </Form>
      </PageContent>
    </Page>
  );
};

export default withRouteChange()(EditRequestJobPosting);
